import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ProductCard from '@/components/feature/ProductCard';
import { categories, products } from '@/mocks/products';
import NotFound from '@/pages/NotFound';

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryProducts = products.filter((p) => p.categorySlug === slug);

  if (!category) {
    return <NotFound />;
  }

  return (
    <>
      <PageMeta
        title={`${category.name} - 爱威奶在线 | 爱威奶${category.name}`}
        description={category.description}
        keywords={`爱威奶${category.name},${category.name}购买,爱威奶在线`}
        canonicalUrl={`https://www.aiweinai.com/category/${category.slug}`}
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={[
            { label: '首页', to: '/' },
            { label: '全部分类', to: '/category' },
            { label: category.name },
          ]} />

          {/* Category Header */}
          <div className="flex flex-col lg:flex-row items-start gap-8 mb-12 mt-6">
            <div className="w-full lg:w-[360px] flex-shrink-0 aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
              <img
                src={category.image}
                alt={category.name}
                title={`${category.name} - 爱威奶在线`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                {category.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                {category.description}
              </p>
              <div className="flex items-center gap-4">
                <span className="text-sm text-foreground-500">
                  共 <strong className="text-primary-600">{categoryProducts.length}</strong> 款产品
                </span>
              </div>
            </div>
          </div>

          {/* Products */}
          <h2 className="text-lg font-semibold text-foreground-900 mb-6 font-heading">
            {category.name}产品列表
          </h2>
          {categoryProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {categoryProducts.map((product) => (
                <ProductCard key={product.id} {...product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无产品</p>
            </div>
          )}

          {/* Related Categories */}
          <div className="mt-16">
            <h2 className="text-lg font-semibold text-foreground-900 mb-6 font-heading">其他分类</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
              {categories.filter((c) => c.slug !== category.slug).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": `${category.name} - 爱威奶在线`,
          "description": category.description,
          "url": `https://www.aiweinai.com/category/${category.slug}`,
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.aiweinai.com" },
              { "@type": "ListItem", "position": 2, "name": "全部分类", "item": "https://www.aiweinai.com/category" },
              { "@type": "ListItem", "position": 3, "name": category.name, "item": `https://www.aiweinai.com/category/${category.slug}` },
            ],
          },
        }),
      }} />
    </>
  );
}